import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  Spin,
  InputNumber,
  Select,
  Upload,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { uploadAudio } from '../services/uploadService';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import {
  getTourStops,
  createTourStop,
  updateTourStop,
  deleteTourStop,
} from '../services/tourStopsService';
import { allPlaces } from '../services/placesService';
import { getTour } from '../services/toursService';
import moment from 'moment';

function TourStopsAdmin() {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const [stops, setStops] = useState([]);
  const [places, setPlaces] = useState([]);
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStop, setEditingStop] = useState(null);
  const [form] = Form.useForm();
  const { Title } = Typography;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [stopsData, placesData, tourData] = await Promise.all([
          getTourStops(tourId),
          allPlaces(),
          getTour(tourId),
        ]);
        setStops(stopsData);
        setPlaces(placesData);
        setTour(tourData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tourId]);

  const fetchStops = async () => {
    try {
      const data = await getTourStops(tourId);
      setStops(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenModal = (stop = null) => {
    if (stop) {
      setEditingStop(stop);
      form.setFieldsValue({
        place_id: stop.place_id,
        stop_number: stop.stop_number,
        description: stop.description || '',
        duration: stop.duration ? moment.duration(stop.duration).asMinutes() : null,
        audio_url: stop.audio_url || '',
      });
    } else {
      setEditingStop(null);
      form.resetFields();
      // Set next stop number
      const nextStopNumber = stops.length > 0 ? Math.max(...stops.map(s => s.stop_number)) + 1 : 1;
      form.setFieldsValue({ stop_number: nextStopNumber });
    }
    setOpenDialog(true);
  };

  const handleCloseModal = () => {
    setOpenDialog(false);
    setEditingStop(null);
    form.resetFields();
  };

  const handleSubmit = async values => {
    const stopData = {
      ...values,
      tour_id: tourId,
      duration: values.duration ? `${values.duration} minutes` : null,
    };

    setLoading(true);
    try {
      if (editingStop) {
        await updateTourStop(editingStop.id, stopData);
      } else {
        await createTourStop(stopData);
      }
      fetchStops();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: 'Are you sure you want to delete this stop?',
      onOk: async () => {
        setLoading(true);
        try {
          await deleteTourStop(id);
          fetchStops();
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Stop #',
      dataIndex: 'stop_number',
      key: 'stop_number',
      sorter: (a, b) => a.stop_number - b.stop_number,
    },
    {
      title: 'Place',
      dataIndex: ['place', 'name'],
      key: 'place',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  if (error) return <Alert message={error} type="error" />;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/tours')}>
            Back to Tours
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            {tour?.name} - Stops
          </Title>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
          Add Stop
        </Button>
      </div>

      <Table columns={columns} dataSource={stops} rowKey="id" pagination={false} />

      <Modal
        title={editingStop ? 'Edit Stop' : 'Add Stop'}
        open={openDialog}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="place_id"
            label="Place"
            rules={[{ required: true, message: 'Please select a place!' }]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {places.map(place => (
                <Select.Option key={place.id} value={place.id}>
                  {place.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="stop_number"
            label="Stop Number"
            rules={[{ required: true, message: 'Please input the stop number!' }]}
          >
            <InputNumber min={1} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="duration" label="Duration (minutes)">
            <InputNumber min={1} />
          </Form.Item>
          <Form.Item name="audio_url" label="Audio">
            <Upload
              maxCount={1}
              accept="audio/*"
              beforeUpload={async file => {
                try {
                  const url = await uploadAudio(file);
                  form.setFieldsValue({ audio_url: url });
                  message.success('Audio uploaded successfully');
                } catch (err) {
                  message.error('Failed to upload audio');
                }
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>Upload Audio</Button>
            </Upload>
            {form.getFieldValue('audio_url') && (
              <audio
                controls
                src={form.getFieldValue('audio_url')}
                style={{ marginTop: 8, width: '100%' }}
              >
                <track kind="captions" src="" label="English" />
                Your browser does not support the audio element.
              </audio>
            )}
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingStop ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default TourStopsAdmin;
